import { contrastRatio, generatePaletteScale, type PaletteScale } from './palette'

/**
 * The decorative hues (owner palette, 2026-10-25), used only on icon tiles, step markers and illustration panels.
 * Colour marks things; it never carries meaning alone.
 */
export const DECO_HUES = {
  green: '#296948',
  blue: '#5a7ca6',
  peach: '#eea591',
} as const

// The tints and the text colour of a hue are generated from this base. Green leans slightly towards teal
// (#296948 -> #2a6b5a) so a green icon tile does not read as the green success badge (status hues stay reserved
// for status). `solid` stays the owner's exact hex.
const SCALE_BASE: Record<keyof typeof DECO_HUES, string> = {
  green: '#2a6b5a',
  blue: '#5a7ca6',
  peach: '#eea591',
}

export type DecoHue = keyof typeof DECO_HUES

export type DecoSlots = { bg: string; fg: string; solid: string }
export type DecoMode = 'light' | 'dark'

const MIN_TEXT_CONTRAST = 4.5

// Same step pairs as the status colours (`subtle` / `fg` slots): light bg 100 + fg 700, dark bg 900 + fg 300. When a
// hue's fg does not reach 4.5:1 on its tint (peach is pale), the next stronger step is used.
const BG_STEP = { light: 100, dark: 900 } as const
const FG_STEPS = {
  light: [700, 800, 900, 950],
  dark: [300, 200, 100, 50],
} as const satisfies Record<DecoMode, Array<keyof PaletteScale>>

const slots = (hue: DecoHue, mode: DecoMode): DecoSlots => {
  const scale = generatePaletteScale(SCALE_BASE[hue])
  const bg = scale[BG_STEP[mode]]
  // The first step that clears 4.5:1 on the tint; the last candidate is the strongest fallback.
  const fg =
    FG_STEPS[mode].map((s) => scale[s]).find((c) => contrastRatio(c, bg) >= MIN_TEXT_CONTRAST) ??
    scale[FG_STEPS[mode][FG_STEPS[mode].length - 1]!]
  return { bg, fg, solid: DECO_HUES[hue] }
}

/** Resolved hex values per hue and colour mode (used by the semantic tokens and by the contrast test). */
export const decoValues = Object.fromEntries(
  (Object.keys(DECO_HUES) as DecoHue[]).map((hue) => [hue, { light: slots(hue, 'light'), dark: slots(hue, 'dark') }])
) as Record<DecoHue, Record<DecoMode, DecoSlots>>

/** `deco.{hue}.{bg|fg|solid}` semantic colour tokens. */
export const decoSemanticColors = Object.fromEntries(
  (Object.keys(DECO_HUES) as DecoHue[]).map((hue) => [
    hue,
    Object.fromEntries(
      (['bg', 'fg', 'solid'] as const).map((slot) => [
        slot,
        { value: { _light: decoValues[hue].light[slot], _dark: decoValues[hue].dark[slot] } },
      ])
    ),
  ])
)
